/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

// OIDC error codes for "prompt=none couldn't be honoured silently"
export const OIDC_NO_INTERACTION_ERROR_CODES = [
  'login_required',
  'interaction_required',
  'consent_required',
];

export const buildSilentLoginUrl = (loginUrl: string): string =>
  `${loginUrl}${loginUrl.includes('?') ? '&' : '?'}prompt=none`;

// Force-auth already redirects to /login on a live IdP session, so silent login is redundant there. Keep them mutually exclusive
export const isSilentLoginEnabled = (config?: {
  GW_FEATURE_SILENT_LOGIN?: boolean;
  GW_FEATURE_FORCE_AUTHENTICATION?: boolean;
}): boolean =>
  Boolean(
    config?.GW_FEATURE_SILENT_LOGIN && !config?.GW_FEATURE_FORCE_AUTHENTICATION,
  );

const SILENT_LOGIN_ATTEMPT_STORAGE_KEY = 'gw_silent_login_attempted_at';
const SILENT_LOGIN_COOLDOWN_MS = 5 * 60 * 1000;

// Limits retries if the IdP doesn't honour prompt=none
export const hasRecentSilentLoginAttempt = (): boolean => {
  const lastAttempt = Number(
    localStorage.getItem(SILENT_LOGIN_ATTEMPT_STORAGE_KEY),
  );
  return (
    Boolean(lastAttempt) && Date.now() - lastAttempt < SILENT_LOGIN_COOLDOWN_MS
  );
};

export const recordSilentLoginAttempt = (): void => {
  localStorage.setItem(SILENT_LOGIN_ATTEMPT_STORAGE_KEY, String(Date.now()));
};

export const clearSilentLoginAttempt = (): void => {
  localStorage.removeItem(SILENT_LOGIN_ATTEMPT_STORAGE_KEY);
};

const SILENT_LOGIN_SUPPRESSED_STORAGE_KEY = 'gw_silent_login_suppressed';

// Set on logout; never expires on its own (unlike the cooldown above), only clearSilentLoginSuppression lifts it
export const suppressSilentLogin = (): void => {
  localStorage.setItem(SILENT_LOGIN_SUPPRESSED_STORAGE_KEY, 'true');
};

export const isSilentLoginSuppressed = (): boolean =>
  localStorage.getItem(SILENT_LOGIN_SUPPRESSED_STORAGE_KEY) === 'true';

export const clearSilentLoginSuppression = (): void => {
  localStorage.removeItem(SILENT_LOGIN_SUPPRESSED_STORAGE_KEY);
};

// Tells tabs already open to log out now (suppressSilentLogin only blocks future attempts)
const LOGOUT_BROADCAST_CHANNEL_NAME = 'gw_auth_logout';

export const broadcastLogout = (): void => {
  const channel = new BroadcastChannel(LOGOUT_BROADCAST_CHANNEL_NAME);
  channel.postMessage('logout');
  channel.close();
};

export const createLogoutBroadcastChannel = (): BroadcastChannel =>
  new BroadcastChannel(LOGOUT_BROADCAST_CHANNEL_NAME);
